import { api } from "@/lib/apiClient";
import { getPendingOperations, removePendingOperation, clearPendingOperations } from "./offlineStorage";
import {
  initDB,
  STORES,
  putMany,
  getAll,
  getPendingActions,
  markActionSynced,
  markActionFailed,
  removeSyncedActions,
  clearExpiredCache,
  PendingAction,
} from "./offlineDB";
import { toast } from "sonner";
import logger from "./logger";
import { getApiUrl } from "./config";

const API_URL = getApiUrl();

// Sync state types
export type SyncStatus = 'idle' | 'syncing' | 'success' | 'error' | 'offline';

export interface SyncState {
  status: SyncStatus;
  lastSync: number | null;
  pendingCount: number;
  isOnline: boolean;
}

type SyncListener = (state: SyncState) => void;

// Singleton sync state
let syncState: SyncState = {
  status: 'idle',
  lastSync: null,
  pendingCount: 0,
  isOnline: navigator.onLine,
};

const syncListeners = new Set<SyncListener>();

function notifyListeners() {
  syncListeners.forEach(listener => listener({ ...syncState }));
}

export function subscribeSyncState(listener: SyncListener): () => void {
  syncListeners.add(listener);
  listener({ ...syncState });
  return () => syncListeners.delete(listener);
}

export function getSyncState(): SyncState {
  return { ...syncState };
}

function updateSyncState(partial: Partial<SyncState>) {
  syncState = { ...syncState, ...partial };
  notifyListeners();
}

// Update pending count from both localStorage and IndexedDB
export async function updatePendingCount(): Promise<number> {
  try {
    const localStorageOps = getPendingOperations();
    let indexedDBOps: PendingAction[] = [];
    try {
      indexedDBOps = await getPendingActions();
    } catch {
      // IndexedDB might not be initialized yet
    }
    const count = localStorageOps.length + indexedDBOps.length;
    updateSyncState({ pendingCount: count });
    return count;
  } catch {
    return syncState.pendingCount;
  }
}

// Sync pending operations from localStorage (legacy)
export async function syncPendingOperations() {
  const operations = getPendingOperations();

  if (operations.length === 0) {
    return { success: true, synced: 0, failed: 0 };
  }

  let synced = 0;
  let failed = 0;
  const failedOperations: string[] = [];

  for (const operation of operations) {
    try {
      if (operation.type === "work_order" && operation.action === "create") {
        const { error } = await api.from("work_orders").insert(operation.data);
        if (error) throw error;
      } else if (operation.type === "process_record" && operation.action === "create") {
        const { error } = await api.from("process_records").insert(operation.data);
        if (error) throw error;
      } else if (operation.type === "process_update" && operation.action === "update") {
        const { error } = await api
          .from("process_records")
          .update(operation.data.updates)
          .eq("id", operation.data.id);
        if (error) throw error;
      }

      removePendingOperation(operation.id);
      synced++;
    } catch (error) {
      logger.error("Failed to sync operation:", operation, error);
      failed++;
      failedOperations.push(operation.id);
    }
  }

  return { success: failed === 0, synced, failed, failedOperations };
}

// Sync pending actions from IndexedDB
async function syncIndexedDBActions(): Promise<{ synced: number; failed: number }> {
  let synced = 0;
  let failed = 0;

  try {
    const actions = await getPendingActions();

    for (const action of actions) {
      try {
        const url = `${API_URL}${action.endpoint}`;
        const response = await fetch(url, {
          method: action.method,
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: action.method !== 'DELETE' ? JSON.stringify(action.data) : undefined,
        });

        if (response.ok) {
          await markActionSynced(action.id);
          synced++;
        } else {
          throw new Error(`HTTP ${response.status}`);
        }
      } catch (error: any) {
        await markActionFailed(action.id, error.message);
        failed++;
      }
    }

    // Clean up synced actions
    await removeSyncedActions();
  } catch (error) {
    logger.error('[SyncService] IndexedDB sync error:', error);
  }

  return { synced, failed };
}

// Full sync (both localStorage and IndexedDB)
export async function fullSync(): Promise<{ synced: number; failed: number }> {
  if (!navigator.onLine) {
    updateSyncState({ status: 'offline', isOnline: false });
    return { synced: 0, failed: 0 };
  }

  updateSyncState({ status: 'syncing', isOnline: true });

  try {
    // Sync localStorage operations
    const localResult = await syncPendingOperations();

    // Sync IndexedDB actions
    const indexedResult = await syncIndexedDBActions();

    // Clear expired cache
    await clearExpiredCache();

    const totalSynced = localResult.synced + indexedResult.synced;
    const totalFailed = localResult.failed + indexedResult.failed;

    updateSyncState({
      status: totalFailed === 0 ? 'success' : 'error',
      lastSync: Date.now(),
    });

    await updatePendingCount();

    return { synced: totalSynced, failed: totalFailed };
  } catch (error) {
    logger.error('[SyncService] Full sync error:', error);
    updateSyncState({ status: 'error' });
    return { synced: 0, failed: 0 };
  }
}

// Cache data for offline use
export async function cacheForOffline<T extends { id: string }>(
  storeName: typeof STORES[keyof typeof STORES],
  data: T[]
): Promise<void> {
  try {
    await putMany(storeName, data);
    logger.info(`[SyncService] Cached ${data.length} items to ${storeName}`);
  } catch (error) {
    logger.error(`[SyncService] Error caching to ${storeName}:`, error);
  }
}

// Get cached data
export async function getCachedData<T>(
  storeName: typeof STORES[keyof typeof STORES]
): Promise<T[]> {
  try {
    return await getAll<T>(storeName);
  } catch (error) {
    logger.error(`[SyncService] Error getting cached data from ${storeName}:`, error);
    return [];
  }
}

export function setupAutoSync() {
  let syncInterval: NodeJS.Timeout | null = null;
  let toastId: string | number | undefined;

  const handleOnline = async () => {
    logger.log("[SyncService] Connection restored, syncing pending operations...");
    updateSyncState({ isOnline: true, status: 'syncing' });

    toastId = toast.loading("Sincronizando datos...", { id: 'sync-toast' });

    try {
      const result = await fullSync();

      if (result.synced > 0) {
        toast.success(`${result.synced} operaciones sincronizadas`, {
          id: 'sync-toast',
          duration: 3000
        });
      } else if (result.failed === 0) {
        toast.dismiss('sync-toast');
      }

      if (result.failed > 0) {
        toast.error(`${result.failed} operaciones fallaron. Se reintentarán`, {
          id: 'sync-toast',
          duration: 5000
        });
      }

      // Set up periodic sync every 30 seconds when online
      if (syncInterval) clearInterval(syncInterval);
      syncInterval = setInterval(async () => {
        if (navigator.onLine) {
          const count = await updatePendingCount();
          if (count > 0) {
            const periodicResult = await fullSync();
            if (periodicResult.synced > 0) {
              toast.success(`${periodicResult.synced} sincronizadas en segundo plano`, {
                duration: 2000
              });
            }
          }
        }
      }, 30 * 1000); // 30 seconds
    } catch (error) {
      logger.error("[SyncService] Sync error:", error);
      toast.error("Error al sincronizar. Se reintentará", { id: 'sync-toast' });
      updateSyncState({ status: 'error' });
    }
  };

  const handleOffline = () => {
    logger.log("[SyncService] Connection lost, switching to offline mode");
    updateSyncState({ isOnline: false, status: 'offline' });
    toast.info("Modo sin conexión: Los datos se guardarán localmente", {
      duration: 4000,
    });
    if (syncInterval) {
      clearInterval(syncInterval);
      syncInterval = null;
    }
  };

  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);

  // Initialize IndexedDB
  initDB().catch(error => {
    logger.error("[SyncService] Failed to initialize IndexedDB:", error);
  });

  // Initial state update
  updateSyncState({ isOnline: navigator.onLine });
  updatePendingCount();

  // Initial sync if online
  if (navigator.onLine) {
    // Delay initial sync to let the app load
    setTimeout(handleOnline, 2000);
  }

  return () => {
    window.removeEventListener("online", handleOnline);
    window.removeEventListener("offline", handleOffline);
    if (syncInterval) clearInterval(syncInterval);
  };
}

// Re-export STORES for convenience
export { STORES } from "./offlineDB";
